import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Users, CalendarCheck, ChartPie, ChartBar, CalendarPlus } from '@phosphor-icons/react';

import { UserManagementTab } from '../components/admin/UserManagementTab';
import { EventSettingsTab } from '../components/admin/EventSettingsTab';
import { EventDashboardTab } from '../components/admin/EventDashboardTab';
import { ReportsTab } from '../components/admin/ReportsTab';
import { SchedulingTab } from '../components/admin/SchedulingTab';

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

export default function AdminPage() {
  const { token, user } = useAuth();
  const isQAQI = user?.role === 'qa_qi';
  const [activeTab, setActiveTab] = useState(isQAQI ? 'reports' : 'users');

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-page">
      <header className="bg-white border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" /> Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
            <span className="text-sm text-slate-500">Admin: {user?.first_name} {user?.last_name}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">{isQAQI ? 'QA/QI Reports' : 'Admin Suite'}</h1>
          <p className="text-slate-500 text-sm mt-1">{isQAQI ? 'View and generate reports' : 'Manage users and generate reports'}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {!isQAQI && (
            <TabsList className="bg-white border border-slate-200 p-1 no-print">
              <TabsTrigger value="users" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm" data-testid="users-tab">
                <Users size={18} className="mr-2" /> User Management
              </TabsTrigger>
              <TabsTrigger value="events" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm" data-testid="events-tab">
                <CalendarCheck size={18} className="mr-2" /> Event Settings
              </TabsTrigger>
              <TabsTrigger value="scheduling" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm" data-testid="scheduling-tab">
                <CalendarPlus size={18} className="mr-2" /> Scheduling
              </TabsTrigger>
              <TabsTrigger value="event-dashboard" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm" data-testid="event-dashboard-tab">
                <ChartPie size={18} className="mr-2" /> Event Dashboard
              </TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm" data-testid="reports-tab">
                <ChartBar size={18} className="mr-2" /> Reports
              </TabsTrigger>
            </TabsList>
          )}

          {!isQAQI && <TabsContent value="users"><UserManagementTab /></TabsContent>}
          {!isQAQI && <TabsContent value="events"><EventSettingsTab token={token} /></TabsContent>}
          {!isQAQI && <TabsContent value="scheduling"><SchedulingTab /></TabsContent>}
          {!isQAQI && <TabsContent value="event-dashboard"><EventDashboardTab token={token} /></TabsContent>}
          <TabsContent value="reports"><ReportsTab token={token} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
